import { useNavigate } from "@tanstack/react-router";
import { Building2, Sparkles } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { SpriteLibraryPanel } from "@/components/admin/SpriteLibraryPanel";
import { Button } from "@/components/ui/button";
import { AvatarStudio, randomAvatarColors } from "@/components/vyllage/AvatarStudio";
import {
  DEFAULT_COLORS,
  loadPersisted,
  MIGRATED_KEY,
  UNLOCKS,
} from "@/components/vyllage/constants";
import { OfficePreview } from "@/components/vyllage/OfficePreview";
import { OfficeStudio, type OfficeStudioValues } from "@/components/vyllage/OfficeStudio";
import { PresenceStrip } from "@/components/vyllage/PresenceStrip";
import { VyllageAvatarPreview } from "@/components/vyllage/vyllage-elements";
import type { AvatarColors } from "@/components/vyllage/world-engine";
import {
  useClaimPersonalOffice,
  useMyPersonalOffice,
  useUpdatePersonalOffice,
} from "@/hooks/use-personal-offices";
import { useUpdateVyllageProfile, useVyllageProfile } from "@/hooks/use-vyllage-profile";
import { useSession } from "@/lib/auth-client";
import { normalizeAvatarLook } from "@/lib/office/avatar-look";
import { DEFAULT_OFFICE_THEME, normalizeOfficeTheme } from "@/lib/office/office-theme";

/** Which ready-room surface is shown (each is its own sidebar route). */
export type ReadyRoomPanel = "avatar" | "office" | "sprites";

const LOOK_AUTOSAVE_MS = 700;
const OFFICE_AUTOSAVE_MS = 900;

function looksEqual(a: AvatarColors, b: AvatarColors): boolean {
  const keys = Object.keys(a) as Array<keyof AvatarColors>;
  return keys.every((k) => a[k] === b[k]);
}

function officeDraftEqual(a: OfficeStudioValues, b: OfficeStudioValues): boolean {
  if (a.displayName !== b.displayName) return false;
  if (a.doorStatus !== b.doorStatus) return false;
  if (a.wallArtFileId !== b.wallArtFileId) return false;
  if (a.wallArtPlaceholder !== b.wallArtPlaceholder) return false;
  if (a.theme.wallColor !== b.theme.wallColor) return false;
  if (a.theme.rugColor !== b.theme.rugColor) return false;
  if (a.decorIds.length !== b.decorIds.length) return false;
  return a.decorIds.every((id, i) => id === b.decorIds[i]);
}

export function ReadyRoomShell({ panel }: { panel: ReadyRoomPanel }) {
  const navigate = useNavigate();
  const { data: session } = useSession();
  const isAdmin = session?.user?.role === "admin";
  const {
    data: profile,
    isLoading: profileLoading,
    isFetching: profileFetching,
    isError: profileError,
    error: profileErr,
    refetch,
  } = useVyllageProfile();
  const updateProfile = useUpdateVyllageProfile();
  const { data: myOffice, isLoading: officeLoading } = useMyPersonalOffice();
  const claimOffice = useClaimPersonalOffice();
  const updateOffice = useUpdatePersonalOffice();

  const [name, setName] = useState(session?.user.name ?? "");
  const [colors, setColors] = useState<AvatarColors>(DEFAULT_COLORS);
  const [officeDraft, setOfficeDraft] = useState<OfficeStudioValues | null>(null);
  const [migrated, setMigrated] = useState(false);
  const [entering, setEntering] = useState(false);
  const [lookSaveLabel, setLookSaveLabel] = useState<"idle" | "saving" | "saved">("idle");
  const [officeSaveLabel, setOfficeSaveLabel] = useState<"idle" | "saving" | "saved">("idle");
  const [claimBurst, setClaimBurst] = useState(false);

  const claimDesk = useCallback(() => {
    claimOffice.mutate(undefined, {
      onSuccess: () => {
        try {
          sessionStorage.setItem("vy_claim_burst", "1");
        } catch {
          // ignore
        }
        void navigate({ to: "/dashboard/office" });
      },
    });
  }, [claimOffice, navigate]);

  // Confetti once after a successful claim (survives panel navigation).
  useEffect(() => {
    if (panel !== "office") return;
    try {
      if (sessionStorage.getItem("vy_claim_burst") !== "1") return;
      sessionStorage.removeItem("vy_claim_burst");
    } catch {
      return;
    }
    setClaimBurst(true);
    const t = window.setTimeout(() => setClaimBurst(false), 1800);
    return () => window.clearTimeout(t);
  }, [panel]);

  // Non-admins hitting /dashboard/sprites are bounced to avatar.
  useEffect(() => {
    if (panel === "sprites" && !isAdmin) {
      void navigate({ to: "/dashboard/avatar" });
    }
  }, [isAdmin, panel, navigate]);

  /** Last successfully persisted look snapshot (for dirty compare). */
  const savedLookRef = useRef<{ name: string; colors: AvatarColors } | null>(null);
  const savedOfficeRef = useRef<OfficeStudioValues | null>(null);
  const lookTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const officeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lookInflightRef = useRef<Promise<unknown> | null>(null);
  const officeInflightRef = useRef<Promise<unknown> | null>(null);
  const skipLookAutosaveRef = useRef(true);
  const skipOfficeAutosaveRef = useRef(true);

  // Latest draft for dirty checks inside effects (avoid remount stomping mid-edit).
  const draftLookRef = useRef({ name, colors });
  draftLookRef.current = { name, colors };

  // Hydrate local form state from server profile.
  // Skip when cache already matches saved snapshot, or when local draft is dirty.
  useEffect(() => {
    if (!profile) return;
    const n = profile.displayName;
    const c = normalizeAvatarLook(profile.avatarColors);
    const saved = savedLookRef.current;
    if (saved && saved.name === n && looksEqual(saved.colors, c)) {
      return;
    }
    const draft = draftLookRef.current;
    if (
      saved &&
      (draft.name !== saved.name || !looksEqual(normalizeAvatarLook(draft.colors), saved.colors))
    ) {
      return;
    }
    setName(n);
    setColors(c);
    savedLookRef.current = { name: n, colors: c };
    skipLookAutosaveRef.current = true;
  }, [profile]);

  // One-time localStorage → server look migration (no progression fields)
  useEffect(() => {
    if (!profile || migrated) return;
    try {
      if (localStorage.getItem(MIGRATED_KEY) === "1") {
        setMigrated(true);
        return;
      }
    } catch {
      setMigrated(true);
      return;
    }
    const local = loadPersisted();
    if (!local) {
      try {
        localStorage.setItem(MIGRATED_KEY, "1");
      } catch {
        // ignore
      }
      setMigrated(true);
      return;
    }
    const serverFresh =
      !profile.lookSavedAt &&
      profile.avatarColors.style === DEFAULT_COLORS.style &&
      profile.avatarColors.shirt === DEFAULT_COLORS.shirt;
    if (serverFresh && local.name !== "You") {
      const displayName = local.name.slice(0, 64);
      const avatarColors = normalizeAvatarLook(local.colors);
      updateProfile.mutate(
        { displayName, avatarColors },
        {
          onSuccess: () => {
            setName(displayName);
            setColors(avatarColors);
            savedLookRef.current = { name: displayName, colors: avatarColors };
          },
          onSettled: () => {
            try {
              localStorage.setItem(MIGRATED_KEY, "1");
            } catch {
              // ignore
            }
            setMigrated(true);
          },
        },
      );
    } else {
      try {
        localStorage.setItem(MIGRATED_KEY, "1");
      } catch {
        // ignore
      }
      setMigrated(true);
    }
  }, [profile, migrated, updateProfile]);

  const draftOfficeRef = useRef(officeDraft);
  draftOfficeRef.current = officeDraft;

  useEffect(() => {
    if (!myOffice) {
      setOfficeDraft(null);
      savedOfficeRef.current = null;
      return;
    }
    const next: OfficeStudioValues = {
      displayName: myOffice.displayName,
      doorStatus: myOffice.doorStatus,
      decorIds: myOffice.decorIds,
      wallArtFileId: myOffice.wallArtFileId ?? null,
      wallArtPlaceholder: myOffice.wallArtPlaceholder ?? null,
      theme: normalizeOfficeTheme(myOffice.theme ?? DEFAULT_OFFICE_THEME),
    };
    const saved = savedOfficeRef.current;
    if (saved && officeDraftEqual(saved, next)) {
      return;
    }
    // Don't stomp mid-edit when a refetch races with a dirty tray.
    const draft = draftOfficeRef.current;
    if (saved && draft && !officeDraftEqual(saved, draft)) {
      return;
    }
    setOfficeDraft(next);
    savedOfficeRef.current = next;
    skipOfficeAutosaveRef.current = true;
  }, [myOffice]);

  const flushLook = useCallback(
    async (opts?: { forceConfirm?: boolean }) => {
      if (lookTimerRef.current) {
        clearTimeout(lookTimerRef.current);
        lookTimerRef.current = null;
      }
      if (lookInflightRef.current) {
        try {
          await lookInflightRef.current;
        } catch {
          // error toasted by mutation
        }
      }
      const displayName = (name.trim() || session?.user.name || "Teammate").slice(0, 64);
      const avatarColors = normalizeAvatarLook(colors);
      const saved = savedLookRef.current;
      const contentUnchanged =
        saved !== null && saved.name === displayName && looksEqual(saved.colors, avatarColors);
      // Always PATCH when look is not yet confirmed so lookSavedAt is stamped
      // (user may Step into HQ on defaults without editing).
      const needsConfirm = opts?.forceConfirm || !profile?.lookSavedAt;
      if (contentUnchanged && !needsConfirm) {
        return;
      }
      setLookSaveLabel("saving");
      const p = updateProfile.mutateAsync({ displayName, avatarColors }).then((data) => {
        setName(data.displayName);
        setColors(normalizeAvatarLook(data.avatarColors));
        savedLookRef.current = {
          name: data.displayName,
          colors: normalizeAvatarLook(data.avatarColors),
        };
        setLookSaveLabel("saved");
        return data;
      });
      lookInflightRef.current = p;
      try {
        await p;
      } finally {
        if (lookInflightRef.current === p) lookInflightRef.current = null;
      }
    },
    [name, colors, session?.user.name, updateProfile, profile?.lookSavedAt],
  );

  const flushOffice = useCallback(async () => {
    if (officeTimerRef.current) {
      clearTimeout(officeTimerRef.current);
      officeTimerRef.current = null;
    }
    if (officeInflightRef.current) {
      try {
        await officeInflightRef.current;
      } catch {
        // error toasted by mutation
      }
    }
    if (!myOffice || !officeDraft) return;
    const next: OfficeStudioValues = {
      displayName: officeDraft.displayName.trim().slice(0, 48) || myOffice.displayName,
      doorStatus: officeDraft.doorStatus,
      decorIds: officeDraft.decorIds,
      wallArtFileId: officeDraft.wallArtFileId,
      wallArtPlaceholder: officeDraft.wallArtPlaceholder,
      theme: normalizeOfficeTheme(officeDraft.theme),
    };
    const saved = savedOfficeRef.current;
    if (saved && officeDraftEqual(saved, next)) return;

    setOfficeSaveLabel("saving");
    const p = updateOffice
      .mutateAsync({
        id: myOffice.id,
        displayName: next.displayName,
        doorStatus: next.doorStatus,
        decorIds: next.decorIds,
        wallArtFileId: next.wallArtFileId,
        wallArtPlaceholder: next.wallArtPlaceholder,
        theme: next.theme,
      })
      .then(() => {
        savedOfficeRef.current = next;
        setOfficeDraft(next);
        setOfficeSaveLabel("saved");
      });
    officeInflightRef.current = p;
    try {
      await p;
    } finally {
      if (officeInflightRef.current === p) officeInflightRef.current = null;
    }
  }, [myOffice, officeDraft, updateOffice]);

  // Debounced look autosave
  useEffect(() => {
    if (!profile) return;
    if (skipLookAutosaveRef.current) {
      skipLookAutosaveRef.current = false;
      return;
    }
    const saved = savedLookRef.current;
    const displayName = (name.trim() || session?.user.name || "Teammate").slice(0, 64);
    const avatarColors = normalizeAvatarLook(colors);
    if (saved && saved.name === displayName && looksEqual(saved.colors, avatarColors)) {
      return;
    }
    if (lookTimerRef.current) clearTimeout(lookTimerRef.current);
    lookTimerRef.current = setTimeout(() => {
      lookTimerRef.current = null;
      void flushLook().catch(() => {
        setLookSaveLabel("idle");
      });
    }, LOOK_AUTOSAVE_MS);
    return () => {
      if (lookTimerRef.current) clearTimeout(lookTimerRef.current);
    };
  }, [name, colors, profile, session?.user.name, flushLook]);

  // Debounced office autosave
  useEffect(() => {
    if (!myOffice || !officeDraft) return;
    if (skipOfficeAutosaveRef.current) {
      skipOfficeAutosaveRef.current = false;
      return;
    }
    const saved = savedOfficeRef.current;
    if (saved && officeDraftEqual(saved, officeDraft)) return;
    if (officeTimerRef.current) clearTimeout(officeTimerRef.current);
    officeTimerRef.current = setTimeout(() => {
      officeTimerRef.current = null;
      void flushOffice().catch(() => {
        setOfficeSaveLabel("idle");
      });
    }, OFFICE_AUTOSAVE_MS);
    return () => {
      if (officeTimerRef.current) clearTimeout(officeTimerRef.current);
    };
  }, [officeDraft, myOffice, flushOffice]);

  const stepIntoHq = useCallback(async () => {
    if (!profile) {
      toast.error("Still loading your look — try again in a moment");
      return;
    }
    setEntering(true);
    try {
      // Force confirm so defaults still stamp lookSavedAt before ready=1.
      await flushLook({ forceConfirm: true });
      await flushOffice();
      await navigate({ to: "/office", search: { ready: "1" } });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not save before entering");
    } finally {
      setEntering(false);
    }
  }, [flushLook, flushOffice, navigate, profile]);

  const onColorChange = useCallback((key: keyof AvatarColors, value: string) => {
    setColors((prev) => ({ ...prev, [key]: value }));
  }, []);

  const lookReady = Boolean(profile?.lookSavedAt) || lookSaveLabel === "saved";
  const officeReady = Boolean(myOffice);
  // Never enter until profile hydrated — avoid stomping server look with defaults.
  const canEnter = Boolean(profile) && !profileLoading && !profileError;

  const points = profile?.points ?? 0;
  const streak = profile?.streak ?? 0;
  const unlockedIds = profile?.unlockedIds ?? [];
  const nextUnlock = useMemo(() => UNLOCKS.find((u) => u.pts > points), [points]);

  const voiceLine = !lookReady
    ? "Pick a look so teammates know it’s you."
    : !officeReady
      ? "Optional: claim a desk before you go in."
      : "Looking sharp. Ready when you are.";

  const lookDirty = (() => {
    const saved = savedLookRef.current;
    if (!saved) return false;
    const displayName = (name.trim() || session?.user.name || "Teammate").slice(0, 64);
    return saved.name !== displayName || !looksEqual(saved.colors, normalizeAvatarLook(colors));
  })();
  const officeDirty =
    Boolean(officeDraft && savedOfficeRef.current) &&
    officeDraft !== null &&
    savedOfficeRef.current !== null &&
    !officeDraftEqual(savedOfficeRef.current, officeDraft);

  const saveHint =
    lookSaveLabel === "saving" || officeSaveLabel === "saving"
      ? "Saving…"
      : lookDirty || officeDirty
        ? "Unsaved changes…"
        : lookSaveLabel === "saved" || officeSaveLabel === "saved"
          ? "Saved"
          : "Autosaves as you edit";

  const stageTheme = officeDraft?.theme ?? DEFAULT_OFFICE_THEME;
  const displayName = name.trim() || session?.user.name || "Teammate";
  const stageLook = normalizeAvatarLook(colors);

  const panelTitle = panel === "avatar" ? "Avatar" : panel === "office" ? "Office" : "Sprites";
  const panelHint =
    panel === "avatar"
      ? "How teammates see you"
      : panel === "sprites"
        ? "Admin sprite library"
        : "Your desk in HQ";
  const trayTitle = panel === "avatar" ? "Dress up" : panel === "sprites" ? "Library" : "Decorate";

  const stageBody =
    panel === "sprites" && isAdmin ? (
      <div className="flex h-full min-h-0 flex-col bg-[#07122b] p-3 sm:p-4">
        <div className="min-h-0 flex-1 overflow-hidden rounded-xl border border-white/10 bg-card p-3 text-foreground shadow-lg">
          <SpriteLibraryPanel layout="stage" className="h-full" />
        </div>
      </div>
    ) : panel === "avatar" ? (
      <div className="flex h-full min-h-0 flex-col items-center justify-center gap-3 p-4 sm:p-6">
        <div className="rounded-2xl bg-white/5 p-3 ring-1 ring-white/10 sm:p-5">
          <VyllageAvatarPreview colors={stageLook} size={260} spin />
        </div>
        <p className="text-sm font-medium text-white/85">{displayName}</p>
      </div>
    ) : officeLoading ? (
      <p className="flex h-full items-center justify-center p-6 text-sm text-white/50">
        Loading pod…
      </p>
    ) : !myOffice || !officeDraft ? (
      <div className="flex h-full flex-col items-center justify-center gap-4 px-6 py-8 text-center">
        <div className="flex h-32 w-48 items-end justify-center rounded-2xl border border-dashed border-white/25 bg-gradient-to-b from-white/10 to-white/5 pb-4 shadow-inner">
          <Building2 className="h-12 w-12 text-white/40" />
        </div>
        <div>
          <p className="text-base font-semibold text-white">Empty desk</p>
          <p className="mt-1.5 max-w-[18rem] text-sm leading-relaxed text-white/65">
            Claim a free wall pod — paint, furnish, hang a photo.
          </p>
        </div>
        <Button
          size="lg"
          className="shadow-lg shadow-primary/20"
          onClick={claimDesk}
          disabled={claimOffice.isPending}
        >
          {claimOffice.isPending ? "Claiming…" : "Claim this desk"}
        </Button>
      </div>
    ) : (
      <div className="relative flex h-full min-h-0 w-full flex-col items-center justify-center gap-3 p-3 sm:p-5">
        {claimBurst ? <ClaimConfetti /> : null}
        {/* Framed stage so the pod pops against the navy ready-room chrome */}
        <div className="relative w-full max-w-[560px] overflow-hidden rounded-2xl border border-white/15 bg-gradient-to-b from-[#1a335c] via-[#122848] to-[#0c1a36] shadow-[0_20px_50px_rgba(0,0,0,0.45)] ring-1 ring-white/10">
          <div
            className="pointer-events-none absolute inset-0 opacity-40"
            style={{
              backgroundImage:
                "radial-gradient(ellipse 80% 50% at 50% 100%, rgba(45,155,214,0.35), transparent 70%)",
            }}
          />
          <OfficePreview
            displayName={officeDraft.displayName}
            doorStatus={officeDraft.doorStatus}
            decorIds={officeDraft.decorIds}
            wallArtFileId={officeDraft.wallArtFileId}
            wallArtPlaceholder={officeDraft.wallArtPlaceholder}
            theme={normalizeOfficeTheme(stageTheme)}
            className="relative mx-auto block h-auto max-h-full w-full"
            width={540}
            height={380}
            transparentBg
            avatarLook={{
              shirt: stageLook.shirt,
              skin: stageLook.skin,
              pants: stageLook.pants,
              shoes: stageLook.shoes,
              hair: stageLook.hair,
              style: stageLook.style,
            }}
          />
        </div>
        <div className="flex flex-wrap items-center justify-center gap-2 text-center">
          <span className="rounded-full bg-white/10 px-2.5 py-0.5 text-[11px] font-semibold text-white/80">
            Slot · {myOffice.slotKey.replace("office_", "#")}
          </span>
          <span className="text-[11px] text-white/50">
            Up to 4 decor · photo on the back wall · edits autosave
          </span>
        </div>
      </div>
    );

  const trayBody =
    panel === "sprites" && isAdmin ? (
      <div className="space-y-3 text-sm text-muted-foreground">
        <p>
          Browse every furniture sprite shipped with HQ. Selection shows path, footprint, and draw
          mode.
        </p>
        <p>
          To place furniture on the live map, open{" "}
          <button
            type="button"
            className="font-semibold text-primary underline-offset-2 hover:underline"
            onClick={() => void navigate({ to: "/dashboard/map-layout" })}
          >
            Map layout
          </button>
          .
        </p>
        <p className="text-xs">
          Artist swap: drop PNGs on the same paths under{" "}
          <code className="rounded bg-muted px-1 py-0.5 text-[11px]">public/assets/sprites/</code>.
        </p>
      </div>
    ) : panel === "avatar" ? (
      profileLoading ? (
        <p className="text-sm text-muted-foreground">Loading your look…</p>
      ) : (
        <div className="space-y-3">
          {profileError ? (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm">
              <p className="font-medium text-destructive">
                {profileErr instanceof Error ? profileErr.message : "Could not load profile"}
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                disabled={profileFetching}
                onClick={() => void refetch()}
              >
                {profileFetching ? "Retrying…" : "Retry load"}
              </Button>
            </div>
          ) : null}
          <AvatarStudio
            name={name}
            onNameChange={setName}
            colors={colors}
            onColorChange={onColorChange}
            onRandomize={() => setColors(randomAvatarColors())}
            layout="tray"
            showPreview={false}
            disabled={entering}
          />
        </div>
      )
    ) : officeLoading ? (
      <p className="text-sm text-muted-foreground">Loading office…</p>
    ) : !myOffice ? (
      <div className="flex flex-col items-start gap-3 rounded-lg border border-dashed p-4">
        <Building2 className="h-7 w-7 text-primary" />
        <div>
          <p className="font-semibold">No office yet</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Claim a desk on the stage, then decorate here.
          </p>
        </div>
        <Button onClick={claimDesk} disabled={claimOffice.isPending}>
          {claimOffice.isPending ? "Claiming…" : "Claim a free office"}
        </Button>
      </div>
    ) : officeDraft ? (
      <OfficeStudio
        values={officeDraft}
        onChange={(patch) => setOfficeDraft((prev) => (prev ? { ...prev, ...patch } : prev))}
        unlockedIds={unlockedIds}
        points={points}
        disabled={entering}
        showPreview={false}
        layout="tray"
      />
    ) : null;

  if (panel === "sprites" && !isAdmin) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center bg-[#0A1A3A] p-6 text-sm text-white/60">
        Redirecting…
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-[#0A1A3A] text-foreground">
      <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-2 border-b border-white/10 bg-[#07122b] px-3 py-2 md:px-4">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold tracking-wide text-white/50 uppercase">{panelTitle}</p>
          <p className="truncate text-sm text-white/80">{voiceLine}</p>
        </div>
        <PresenceStrip className="order-last w-full sm:order-none sm:w-auto" compact />
        <span className="text-[11px] text-white/45">{saveHint}</span>
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="rounded-full bg-white/10 px-2.5 py-0.5 text-[11px] font-semibold tabular-nums text-white">
            {points} pts
          </span>
          <span className="rounded-full bg-white/10 px-2.5 py-0.5 text-[11px] font-semibold tabular-nums text-white">
            {streak}d
          </span>
          {nextUnlock ? (
            <span className="hidden text-[11px] text-white/45 xl:inline">
              Next · {nextUnlock.name}
            </span>
          ) : (
            <span className="hidden items-center gap-1 text-[11px] text-white/45 xl:inline-flex">
              <Sparkles className="h-3 w-3" /> Maxed
            </span>
          )}
          <Button
            size="default"
            className="shadow-md shadow-primary/25"
            disabled={entering || !canEnter}
            onClick={() => void stepIntoHq()}
          >
            <Building2 className="h-4 w-4" />
            {entering ? "Saving…" : "Step into HQ"}
          </Button>
        </div>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 grid-rows-[minmax(240px,42vh)_minmax(0,1fr)] gap-0 lg:grid-cols-[minmax(0,1fr)_minmax(380px,38%)] lg:grid-rows-1">
        <section className="relative flex min-h-0 flex-col border-b border-white/10 bg-[#07122b] lg:border-r lg:border-b-0">
          <div className="flex shrink-0 items-center justify-between border-b border-white/10 bg-black/30 px-3 py-2">
            <span className="text-xs font-semibold text-white/70">{panelHint}</span>
          </div>
          <div className="min-h-0 flex-1 overflow-hidden">{stageBody}</div>
        </section>

        <section className="flex min-h-0 flex-col bg-card">
          <div className="flex shrink-0 items-center justify-between gap-2 border-b border-border/60 px-3 py-2.5 md:px-4">
            <h2 className="text-xs font-bold tracking-wide text-muted-foreground uppercase">
              {trayTitle}
            </h2>
            <span className="text-[11px] text-muted-foreground">{saveHint}</span>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-3 pb-20 md:px-4 md:pb-4">
            {trayBody}
          </div>
        </section>
      </div>

      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-background/95 p-2.5 backdrop-blur lg:hidden">
        <Button
          size="lg"
          className="w-full"
          disabled={entering || !canEnter}
          onClick={() => void stepIntoHq()}
        >
          <Building2 className="h-4 w-4" />
          {entering ? "Saving…" : "Step into HQ"}
        </Button>
      </div>
    </div>
  );
}

/** Lightweight claim celebration — CSS confetti, no extra deps. */
function ClaimConfetti() {
  const bits = [
    "#2D9BD6",
    "#E8A93C",
    "#2EA86A",
    "#FF6B9D",
    "#64479D",
    "#FFFFFF",
    "#D8443C",
    "#5461AD",
  ];
  return (
    <div className="pointer-events-none absolute inset-0 z-20 overflow-hidden" aria-hidden>
      {bits.map((c, i) => (
        <span
          key={c}
          className="absolute top-0 h-2 w-2 rounded-sm opacity-90"
          style={{
            left: `${8 + i * 11}%`,
            background: c,
            animation: `vyClaimFall 1.4s ease-in ${i * 0.06}s both`,
          }}
        />
      ))}
      <style>{`
        @keyframes vyClaimFall {
          0% { transform: translateY(-12px) rotate(0deg); opacity: 0; }
          15% { opacity: 1; }
          100% { transform: translateY(220px) rotate(240deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
